import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Folder, FileText, Clock, CheckCircle, XCircle, Loader2, Trash2 } from 'lucide-react';
import { useData } from '../context/DataContext';

interface FileUploadProps {
  onDataLoaded: () => void;
}

interface UploadingFile {
  name: string;
  size: number;
  status: 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

const FileUploadNew: React.FC<FileUploadProps> = ({ onDataLoaded }) => {
  const { uploadFiles, isLoading, error } = useData();
  const [dragActive, setDragActive] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format elapsed time
  const formatElapsedTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  // Start timer
  const startTimer = () => {
    const start = Date.now();
    setStartTime(start);
    timerRef.current = setInterval(() => {
      setElapsedTime(Date.now() - start);
    }, 100);
  };

  // Stop timer
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const processFiles = useCallback(async (files: File[]) => {
    // Filter PDF and NetCDF files
    const allowedFiles = files.filter(file => {
      const ext = file.name.toLowerCase();
      return ext.endsWith('.pdf') || ext.endsWith('.nc') || ext.endsWith('.nc4') || ext.endsWith('.netcdf');
    });

    if (allowedFiles.length === 0) {
      alert('No supported files found. Please upload PDF or NetCDF (.nc) files only.');
      return;
    }

    // Initialize uploading files state
    const fileStates: UploadingFile[] = allowedFiles.map(file => ({
      name: file.name,
      size: file.size,
      status: 'uploading',
      progress: 0
    }));
    setUploadingFiles(fileStates);

    // Start timer
    startTimer();

    try {
      // Simulate progress for UI feedback
      const progressInterval = setInterval(() => {
        setUploadingFiles(prev => prev.map(f => ({
          ...f,
          progress: f.status === 'uploading' ? Math.min(f.progress + Math.random() * 15, 90) : f.progress
        })));
      }, 500);

      // Actual upload
      await uploadFiles(allowedFiles);

      clearInterval(progressInterval);

      // Mark all as success
      setUploadingFiles(prev => prev.map(f => ({
        ...f,
        status: 'success',
        progress: 100
      })));

      stopTimer();

      // Auto-clear after 3 seconds
      setTimeout(() => {
        setUploadingFiles([]);
        setElapsedTime(0);
        onDataLoaded();
      }, 3000);

    } catch (err: any) {
      stopTimer();
      console.error('Upload error:', err);
      setUploadingFiles(prev => prev.map(f => ({
        ...f,
        status: 'error',
        error: err.message || 'Upload failed'
      })));
    }
  }, [uploadFiles, onDataLoaded]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const items = Array.from(e.dataTransfer.items || []);
    const files: File[] = [];

    items.forEach(item => {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    });

    if (files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFolderSelect = () => {
    folderInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(Array.from(e.target.files));
    }
  };

  const handleClearAll = () => {
    stopTimer();
    setUploadingFiles([]);
    setElapsedTime(0);
  };

  return (
    <div className="min-h-screen bg-white p-8">
      <div className="max-w-5xl mx-auto">
        {/* Minimal Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-black mb-2">Upload Documents</h1>
          <p className="text-gray-600">
            Upload PDF or NetCDF files or folders to process and analyze
          </p>
        </motion.div>

        {/* Clean Upload Area - No Boxes */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="relative"
        >
          {/* Drag & Drop Zone - Minimal Design */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-2xl transition-all duration-300 ${
              dragActive
                ? 'border-purple-500 bg-purple-50 scale-[1.01]'
                : 'border-gray-300 hover:border-purple-400 bg-gray-50'
            }`}
          >
            <div className="relative p-12 md:p-16">
              {/* Upload Icon - Clean */}
              <motion.div
                animate={{
                  y: dragActive ? -5 : 0,
                  scale: dragActive ? 1.05 : 1
                }}
                className="flex justify-center mb-6"
              >
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Upload size={40} className="text-white" />
                </div>
              </motion.div>

              {/* Upload Text - Minimal */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-black mb-2">
                  {dragActive ? 'Drop files here' : 'Drag & drop files or folders'}
                </h3>
                <p className="text-gray-500 text-sm">
                  PDF and NetCDF files
                </p>
              </div>

              {/* Action Buttons - Clean Design */}
              <div className="flex justify-center gap-4">
                <motion.button
                  onClick={handleFileSelect}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isLoading}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-medium shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FileText size={20} />
                  <span>Files</span>
                </motion.button>

                <motion.button
                  onClick={handleFolderSelect}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isLoading}
                  className="px-6 py-3 bg-white border-2 border-gray-300 hover:border-purple-400 text-gray-700 hover:text-purple-600 rounded-lg font-medium shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Folder size={20} />
                  <span>Folder</span>
                </motion.button>
              </div>

              {/* Hidden Inputs */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.nc,.nc4,.netcdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <input
                ref={folderInputRef}
                type="file"
                multiple
                accept=".pdf,.nc,.nc4,.netcdf"
                // @ts-ignore
                webkitdirectory="true"
                directory="true"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Upload Progress - Clean Design */}
          <AnimatePresence>
            {uploadingFiles.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 bg-white rounded-xl border border-gray-200 shadow-lg p-6"
              >
                {/* Header - Minimal */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {isLoading ? (
                      <Loader2 size={20} className="text-purple-600 animate-spin" />
                    ) : (
                      <CheckCircle size={20} className="text-green-500" />
                    )}
                    <div>
                      <h3 className="text-base font-semibold text-black">
                        {isLoading ? 'Processing...' : 'Complete'}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {uploadingFiles.length} file{uploadingFiles.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {/* Timer - Clean */}
                  {startTime && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                      <Clock size={14} className="text-gray-600" />
                      <span className="text-gray-700 font-mono text-sm font-medium">
                        {formatElapsedTime(elapsedTime)}
                      </span>
                    </div>
                  )}

                  {/* Clear Button */}
                  {!isLoading && (
                    <motion.button
                      onClick={handleClearAll}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
                    >
                      <Trash2 size={18} />
                    </motion.button>
                  )}
                </div>

                {/* File List - Clean */}
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {uploadingFiles.map((file, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText size={18} className="text-purple-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-black font-medium text-sm truncate">{file.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>

                        {/* Status Icon */}
                        <div className="flex-shrink-0 ml-3">
                          {file.status === 'uploading' && (
                            <Loader2 size={18} className="text-purple-600 animate-spin" />
                          )}
                          {file.status === 'success' && (
                            <CheckCircle size={18} className="text-green-500" />
                          )}
                          {file.status === 'error' && (
                            <XCircle size={18} className="text-red-500" />
                          )}
                        </div>
                      </div>

                      {/* Progress Bar - Clean */}
                      {file.status === 'uploading' && (
                        <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${file.progress}%` }}
                            className="h-full bg-gradient-to-r from-purple-600 to-blue-600"
                          />
                        </div>
                      )}

                      {/* Error Message */}
                      {file.status === 'error' && file.error && (
                        <p className="text-xs text-red-600 mt-1.5">{file.error}</p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Display - Clean */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <XCircle size={20} className="text-red-500 flex-shrink-0" />
                <div>
                  <h4 className="text-red-600 font-semibold text-sm mb-0.5">Upload Error</h4>
                  <p className="text-red-500 text-xs">{error}</p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default FileUploadNew;
